import { useStartSubscriptionPriceDrop } from '@/hooks/actions/use-start-subscription-price-drop';
import { VStack } from '@chakra-ui/react';
import type { SubscriptionPriceDropOffer } from '@fluenceur/types';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import SettingsSection from '../SettingsSection';
import { MigrationAction } from '../success-fee-migration/MigrationAction';
import { PriceDropSummary } from './PriceDropSummary';

interface SubscriptionPriceDropCardProps {
  /** Null when the subscription already pays the catalogue price or less. */
  offer: SubscriptionPriceDropOffer | null | undefined;
  isLast?: boolean;
}

/**
 * The lower-price row of the billing settings page.
 *
 * Shopify never lowers a subscription in place, so the merchant approves a
 * replacement at the new price on a Shopify page. Only a brand owner or admin
 * gets the button; everyone else reads who can act. Whether the viewer can
 * act comes from the offer itself, never from a role read here.
 */
export const SubscriptionPriceDropCard: React.FC<SubscriptionPriceDropCardProps> = ({
  offer,
  isLast = false,
}) => {
  const { t } = useTranslation();
  const startPriceDrop = useStartSubscriptionPriceDrop();

  if (!offer) return null;

  // The button keeps spinning after success: the page is about to leave for
  // Shopify, and a second click would only ask for a second replacement.
  const isStarting = startPriceDrop.isPending || startPriceDrop.isSuccess;

  return (
    <SettingsSection title={t('settings.subscription.priceDrop.title')} isLast={isLast}>
      <VStack align="flex-start" gap={3}>
        <PriceDropSummary offer={offer} />
        <MigrationAction
          isAdmin={offer.canApply}
          blockedReasonKey={null}
          isStarting={isStarting}
          onAct={() => startPriceDrop.mutate()}
          actionKey="settings.subscription.priceDrop.action"
          viewerHelperKey="settings.subscription.priceDrop.viewerHelper"
        />
      </VStack>
    </SettingsSection>
  );
};

export default SubscriptionPriceDropCard;
